import React from 'react'
import styled from 'styled-components'
import { Row, Col } from 'antd'
import { 
  IconUsers, 
  IconCash, 
  IconTrendingUp, 
  IconMessageCircle,
  IconWallet,
  IconArrowUpRight,
  IconArrowDownRight,
  IconUserPlus,
  IconUserCheck
} from '@tabler/icons-react'
import { useNotification } from 'app/hooks/useNotification'
import BigNumber from 'bignumber.js'

const DashboardContainer = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
      radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.02) 0%, transparent 50%),
      radial-gradient(circle at 40% 20%, rgba(255, 255, 255, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  position: relative;
  z-index: 1;
`

const StatCard = styled.div<{ $gradient?: string }>`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.2);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => props.$gradient || 'linear-gradient(90deg, #6c757d 0%, #495057 100%)'};
  }
`

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`

const StatTitle = styled.h3`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const StatIconWrapper = styled.div<{ $color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.$color || 'rgba(108, 117, 125, 0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    color: ${props => props.$color ? props.$color.replace('0.1', '1') : '#6c757d'};
  }
`

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`

const StatChange = styled.div<{ $positive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${props => props.$positive ? '#10b981' : '#ef4444'};
  font-size: 14px;
  font-weight: 500;
  
  svg {
    width: 16px;
    height: 16px;
  }
`

const StatDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  margin: 0;
`

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  position: relative;
  z-index: 1;
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`

const SectionTitle = styled.h2`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
`

const Badge = styled.span<{ $color?: string }>`
  background: ${props => props.$color || 'rgba(108, 117, 125, 0.2)'};
  color: ${props => props.$color ? props.$color.replace('0.2', '1').replace('rgba', 'rgb') : '#6c757d'};
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`

const ListItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
`

const ListLabel = styled.span`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
`

const ListValue = styled.span<{ $highlight?: boolean }>`
  color: ${props => props.$highlight ? '#6c757d' : '#ffffff'};
  font-size: 16px;
  font-weight: 600;
`

const QuickStatRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 16px;
`

const QuickStat = styled.div`
  flex: 1;
`

const QuickStatLabel = styled.div`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  margin-bottom: 4px;
`

const QuickStatValue = styled.div`
  color: #ffffff;
  font-size: 20px;
  font-weight: 600;
`

const ModernOverviewBody = () => {
  // Hot reload test - neutral gray colors applied
  const { data: res } = useNotification('/admin/overview', true)
  const userData = res?.users
  const ticketData = res?.ticket

  const totalTickets = BigNumber(ticketData?.waitingCount || 0)
    .plus(ticketData?.reviewCount || 0)
    .plus(ticketData?.completedCount || 0)
    .toNumber()

  const totalUsers = BigNumber(userData?.normalUser || 0)
    .plus(userData?.vipUser || 0)
    .toNumber()

  return (
    <DashboardContainer>
      <StatsGrid>
        <StatCard $gradient="linear-gradient(90deg, #6c757d 0%, #495057 100%)">
          <StatHeader>
            <StatTitle>총 회원수</StatTitle>
            <StatIconWrapper $color="rgba(108, 117, 125, 0.1)">
              <IconUsers size={24} />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{totalUsers.toLocaleString()}</StatValue>
          <StatChange $positive={true}>
            <IconArrowUpRight />
            <span>+{userData?.newUser || 0} 신규</span>
          </StatChange>
          <StatDescription>전체 등록 회원 (업데이트됨)</StatDescription>
        </StatCard>

        <StatCard $gradient="linear-gradient(90deg, #f093fb 0%, #f5576c 100%)">
          <StatHeader>
            <StatTitle>가입 요청</StatTitle>
            <StatIconWrapper $color="rgba(245, 87, 108, 0.1)">
              <IconUserPlus size={24} />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{userData?.joinUser || 0}</StatValue>
          <StatChange $positive={true}>
            <IconArrowUpRight />
            <span>대기중</span>
          </StatChange>
          <StatDescription>승인 대기 회원</StatDescription>
        </StatCard>

        <StatCard $gradient="linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)">
          <StatHeader>
            <StatTitle>VIP 회원</StatTitle>
            <StatIconWrapper $color="rgba(0, 242, 254, 0.1)">
              <IconUserCheck size={24} />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{userData?.vipUser || 0}</StatValue>
          <QuickStatRow>
            <QuickStat>
              <QuickStatLabel>일반회원</QuickStatLabel>
              <QuickStatValue>{userData?.normalUser || 0}</QuickStatValue>
            </QuickStat>
            <QuickStat>
              <QuickStatLabel>신규회원</QuickStatLabel>
              <QuickStatValue>{userData?.newUser || 0}</QuickStatValue>
            </QuickStat>
          </QuickStatRow>
        </StatCard>

        <StatCard $gradient="linear-gradient(90deg, #fa709a 0%, #fee140 100%)">
          <StatHeader>
            <StatTitle>문의 현황</StatTitle>
            <StatIconWrapper $color="rgba(254, 225, 64, 0.1)">
              <IconMessageCircle size={24} />
            </StatIconWrapper>
          </StatHeader>
          <StatValue>{totalTickets}</StatValue>
          <StatChange $positive={false}>
            <IconArrowDownRight />
            <span>{ticketData?.waitingCount || 0} 신규</span>
          </StatChange>
          <StatDescription>전체 문의 건수</StatDescription>
        </StatCard>
      </StatsGrid>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <IconMessageCircle size={20} />
                문의 상세 현황
              </SectionTitle>
              <Badge $color="rgba(239, 68, 68, 0.2)">{ticketData?.waitingCount || 0} 대기</Badge>
            </SectionHeader>
            
            <ListItem>
              <ListLabel>최우선 문의</ListLabel>
              <ListValue $highlight={true}>{ticketData?.type4 || 0}</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>긴급 문의</ListLabel>
              <ListValue>{ticketData?.type3 || 0}</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>일반 문의</ListLabel>
              <ListValue>{ticketData?.type2 || 0}</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>낮은 우선도</ListLabel>
              <ListValue>{ticketData?.type1 || 0}</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>완료된 문의</ListLabel>
              <ListValue>{ticketData?.completedCount || 0}</ListValue>
            </ListItem>
          </SectionCard>
        </Col>

        <Col xs={24} lg={12}>
          <SectionCard>
            <SectionHeader>
              <SectionTitle>
                <IconTrendingUp size={20} />
                실시간 거래 현황
              </SectionTitle>
              <Badge $color="rgba(16, 185, 129, 0.2)">활성</Badge>
            </SectionHeader>
            
            <ListItem>
              <ListLabel>오늘 거래량</ListLabel>
              <ListValue $highlight={true}>₩0</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>입금 건수</ListLabel>
              <ListValue>0</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>출금 건수</ListLabel>
              <ListValue>0</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>대기중 거래</ListLabel>
              <ListValue>0</ListValue>
            </ListItem>
            <ListItem>
              <ListLabel>완료 거래</ListLabel>
              <ListValue>0</ListValue>
            </ListItem>
          </SectionCard>
        </Col>
      </Row>
    </DashboardContainer>
  )
}

export default ModernOverviewBody